import { useState } from "react";
import { api } from "../api";
import { CASE_STATUS_LABELS, type Case, type CaseLink } from "../types";

interface Props {
  caseId: string;
  allCases: Case[];
  links: CaseLink[];
  onChanged: (next: CaseLink[]) => void;
  onNavigateCase: (id: string) => void;
}

export default function LinkedCases({
  caseId,
  allCases,
  links,
  onChanged,
  onNavigateCase,
}: Props) {
  const [adding, setAdding] = useState(false);
  const [pickId, setPickId] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const linkedIds = new Set(links.map((l) => l.id));
  const candidates = allCases.filter((c) => !linkedIds.has(c.id));

  async function refresh() {
    const { links: next } = await api.listLinks(caseId);
    onChanged(next);
  }

  async function addLink() {
    if (!pickId) return;
    setSaving(true);
    setError(null);
    try {
      await api.createLink(caseId, pickId, note.trim() || undefined);
      await refresh();
      setAdding(false);
      setPickId("");
      setNote("");
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSaving(false);
    }
  }

  async function removeLink(linkId: string) {
    if (!window.confirm("Remove this link?")) return;
    try {
      await api.deleteLink(linkId);
      await refresh();
    } catch (err) {
      window.alert((err as Error).message);
    }
  }

  if (links.length === 0 && !adding && candidates.length === 0) return null;

  return (
    <section className="linked-cases">
      <header className="linked-cases-header">
        <span className="linked-cases-title">Linked cases</span>
        {!adding && candidates.length > 0 && (
          <button
            type="button"
            className="linked-cases-add-btn"
            onClick={() => setAdding(true)}
          >
            + Link a case
          </button>
        )}
      </header>

      {links.length > 0 && (
        <div className="linked-cases-chips">
          {links.map((l) => (
            <span key={l.link_id} className="linked-chip">
              <button
                type="button"
                className={`linked-chip-link status-${l.status}`}
                onClick={() => onNavigateCase(l.id)}
                title={l.note ?? undefined}
              >
                <span className="linked-chip-status" />
                {l.name}
                <span className="linked-chip-status-label">
                  {CASE_STATUS_LABELS[l.status]}
                </span>
              </button>
              {l.note && (
                <span className="linked-chip-note" title={l.note}>
                  — {l.note}
                </span>
              )}
              <button
                type="button"
                className="linked-chip-remove"
                onClick={() => removeLink(l.link_id)}
                aria-label="Remove link"
              >
                ✕
              </button>
            </span>
          ))}
        </div>
      )}

      {adding && (
        <div className="linked-cases-form">
          <select
            value={pickId}
            onChange={(e) => setPickId(e.target.value)}
            aria-label="Linked case"
          >
            <option value="">Pick a case…</option>
            {candidates.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
          <input
            type="text"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Optional note about the relationship"
            maxLength={500}
          />
          <button
            type="button"
            className="btn-primary"
            onClick={addLink}
            disabled={!pickId || saving}
          >
            {saving ? "Linking…" : "Link"}
          </button>
          <button
            type="button"
            onClick={() => {
              setAdding(false);
              setPickId("");
              setNote("");
              setError(null);
            }}
          >
            Cancel
          </button>
          {error && <span className="form-error">{error}</span>}
        </div>
      )}
    </section>
  );
}
